'use client';
import React from 'react';
import { type LinkField } from 'lib/types';
import { cn } from 'lib/utils';
import { useScrollDirection } from 'lib/hooks';
import type { IconNames } from 'atoms/Icon/Icons';
import { Icon, Link, Wrapper } from 'ui';
import GlobalHeaderFull from './GlobalHeaderFull';
import GlobalHeaderMobile from './GlobalHeaderMobile';

export type GlobalHeaderProps = {
  className?: string;
  utilityLink: LinkField;
  items: Array<
    | LinkField
    | {
        label: string;
        panelEyebrow: string;
        panelTitle: string;
        panelIntro: string;
        panelLink?: LinkField;
        children: {
          link: LinkField;
          icon?: IconNames;
          arrow?: 'internal' | 'external';
          description: string;
        }[];
      }
  >;
  ctaLink: LinkField;
};

const GlobalHeader = ({ className, ...props }: GlobalHeaderProps) => {
  const { utilityLink } = props;
  const { scrollDirection, isScrolledPast } = useScrollDirection([45, 120]);
  const [isScrolledPastFirst, isScrolledPastExtended] = isScrolledPast;

  // Show sticky navigation when user has scrolled past threshold and is scrolling up
  const isSticky = isScrolledPastFirst && scrollDirection === 'up';

  return (
    <>
      {/* Utility Nav (never sticky) */}
      <div className="relative z-999 h-header-utility-height lg:h-header-utility-height-full flex items-center bg-graphite-900 lg:px-5 lg:border-b lg:border-clay-50/10">
        <Wrapper as="div" variant="header" className="max-lg:px-6">
          <Link
            className="inline-flex items-center gap-1.5 max-lg:text-p4 lg:text-p3 font-bold no-underline"
            {...utilityLink}
          >
            <span>{utilityLink.text}</span>
            <Icon name="open-in-new-arrow" className="h-3 w-auto shrink-0" />
          </Link>
        </Wrapper>
      </div>

      {/* Main Nav (can become sticky) */}
      <header
        className={cn(
          'w-full z-998 transition-all duration-300 ease-in-out',
          {
            'fixed left-0 right-0': isSticky || isScrolledPastExtended,
            ' top-0': isSticky,
            '-top-20 pointer-events-none': isScrolledPastExtended && !isSticky,
            absolute: !isSticky && !isScrolledPastExtended,
          },
          className
        )}
      >
        <GlobalHeaderMobile {...{ isSticky }} {...props} />
        <GlobalHeaderFull {...{ isSticky }} {...props} />
      </header>
    </>
  );
};

export default GlobalHeader;
